import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormikProps } from "formik";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";
import { surfaceSx, primaryBtnSx, secondaryBtnSx } from "./moduleSx";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
  useGetallGradeQuery,
  useGetallSubjectQuery,
  useGetTeamQuery,
  useUploadCourseMaterialMutation,
  useDeleteCourseMaterialMutation,
  useCreateCourseMaterialMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { todayLocal, formatDateOnly, parseDateOnly, presentDateOnly } from "../../../utils/dateOnly";
import AppDatePicker from "../../../Components/DatePicker/AppDatePicker";

// Shared form values shape. Create and Edit both push their formik through
// this body so add/remove of a field only happens in one place.
export type CourseFormValues = {
  id?: number | null;
  courseName: string;
  courseCode: string;
  description: string;
  gradeId: string;
  subjectId: string;
  staffId: string;
  status: string;
  startDate: string; // YYYY-MM-DD via dateOnly helpers
  endDate: string;
  thumbnail: string;
  // Video URL is written to a course_materials row on save (create) or
  // upserted through the existing course-material endpoint (edit).
  videoUrl: string;
};

const MAX_FILE_MB = 10;
const ALLOWED_MIME = new Set(["image/jpeg", "image/jpg", "image/png", "application/pdf"]);
const ALLOWED_EXT = /\.(jpe?g|png|pdf)$/i;

// ── Shared form-field sx ──────────────────────────────────────────
// Every TextField and FormControl on this form consumes this object
// so heights (~48 px), corner radii (10 px), hover borders, and the
// brand-blue focus ring are consistent everywhere. Reusing one
// object also means future modules (Class Create, Assignment Create,
// etc.) can adopt this identical control family without duplication.
const formFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 48,
    borderRadius: "10px",
    bgcolor: "#FFFFFF",
    transition: "box-shadow 160ms ease, border-color 160ms ease",
    "& fieldset": { borderColor: B13.line },
    "&:hover fieldset": { borderColor: tint12(B13.primary) },
    "&.Mui-focused fieldset": {
      borderColor: B13.primary,
      borderWidth: "1px",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(31,111,235,0.15)",
    },
    "&.Mui-error fieldset": {
      borderColor: "#E11D48",
    },
    "&.Mui-error.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(225,29,72,0.14)",
    },
  },
  // The multiline description needs a taller min-height, but the
  // rest of the styling should stay the same. Multiline TextFields
  // ignore minHeight on the root when rows= is set, so this is just
  // a safe base — the multiline container computes its own height.
  "& .MuiOutlinedInput-input": {
    fontSize: 14,
    color: B13.ink,
  },
  "& .MuiInputLabel-root": {
    fontWeight: 500,
    color: B13Ink.ink3,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: B13.primary,
  },
} as const;

// ── Section header used inside the main Course Information card
// to break the form into meaningful groups (Basic Details / Academic
// Setup / Course Period / Course Media). Small blue-tinted icon
// tile + uppercase eyebrow + short supporting line — no fragmenting
// the form into a wall of separate cards.
const FormSectionHeader: React.FC<{
  icon: React.ReactNode;
  eyebrow: string;
  hint?: string;
  first?: boolean;
}> = ({ icon, eyebrow, hint, first }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      mb: 1.75,
      mt: first ? 0 : 3,
      pt: first ? 0 : 3,
      borderTop: first ? "none" : `1px solid ${B13.line}`,
    }}
  >
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: tint12(B13.primary),
        color: B13.primary,
        "& svg": { fontSize: 18 },
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 800,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: B13.primary,
          lineHeight: 1.15,
        }}
      >
        {eyebrow}
      </Typography>
      {hint && (
        <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink3, mt: 0.35 }}>
          {hint}
        </Typography>
      )}
    </Box>
  </Box>
);

type ExistingMaterial = {
  id: number;
  title?: string | null;
  materialType?: string | null;
  filePath?: string | null;
  externalUrl?: string | null;
  orderNumber?: number | null;
};

/**
 * uploadPendingForCreate now returns a real outcome so the parent
 * Create page can decide whether to navigate away or keep the user
 * on the form to retry failed items. The previous void return
 * silently discarded failures, which is exactly why files could
 * appear to have "uploaded" while sitting in an error state.
 */
export type CourseUploadOutcome = { ok: boolean; failedCount: number };
export type CourseFormBodyHandle = {
  uploadPendingForCreate: (newCourseId: number) => Promise<CourseUploadOutcome>;
  persistVideoUrlForCreate: (newCourseId: number, videoUrl: string) => Promise<void>;
  hasPendingFiles: () => boolean;
};

/**
 * Per-file upload lifecycle used inside the Create form. Files are
 * staged locally until the parent triggers Save Course; each item
 * moves through `ready → uploading → (removed on success | error)`
 * so the UI shows a truthful status instead of a generic "Pending"
 * badge that never updates. Keeping this on the client (rather than
 * pushing to a temp bucket on selection) matches the existing backend
 * contract — no schema change required.
 */
type PendingStatus = "ready" | "uploading" | "error";
type PendingItem = {
  /** Stable key for React reconciliation + status updates. */
  key: string;
  file: File;
  status: PendingStatus;
  error?: string;
};

type Props = {
  mode: "create" | "edit";
  formik: FormikProps<CourseFormValues>;
  submitting: boolean;
  submitLabel: string;
  // Only meaningful on Edit — used to render + delete existing attachments,
  // and to pre-populate the video URL field.
  courseId?: number | null;
  existingMaterials?: ExistingMaterial[];
  onMaterialsChanged?: () => void;
};

const CourseFormBody = React.forwardRef<CourseFormBodyHandle, Props>(function CourseFormBody(
  {
    mode,
    formik,
    submitting,
    submitLabel,
    courseId,
    existingMaterials = [],
    onMaterialsChanged,
  },
  ref,
) {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();

  const { data: gradeData } = useGetallGradeQuery({ searchText: "" });
  const { data: subjectData } = useGetallSubjectQuery({ searchText: "" });
  const { data: staffData } = useGetTeamQuery({ searchText: "", id: "" });

  const gradeList = useMemo(
    () => (Array.isArray(gradeData?.data) ? (gradeData!.data as any[]) : []),
    [gradeData],
  );
  const subjectList = useMemo(
    () => (Array.isArray(subjectData?.data) ? (subjectData!.data as any[]) : []),
    [subjectData],
  );
  const staffList = useMemo(
    () => (Array.isArray(staffData?.data) ? (staffData!.data as any[]) : []),
    [staffData],
  );

  const [uploadMaterial, { isLoading: uploading }] = useUploadCourseMaterialMutation();
  const [deleteMaterial] = useDeleteCourseMaterialMutation();
  const [createMaterial] = useCreateCourseMaterialMutation();

  // Track pending file selection separately from the uploader — Create mode
  // holds each item until the course is saved; Edit uploads immediately.
  // Each item carries its own lifecycle status so the row can render
  // an accurate label ("Ready to upload" / "Uploading…" / "Upload failed"),
  // instead of a generic "Pending" badge that never transitions.
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Course id captured once `uploadPendingForCreate` runs — enables
  // per-row retry after a partial failure without re-creating the
  // course row.
  const createdCourseIdRef = useRef<number | null>(null);
  // Simple key generator — safe under strict mode because it doesn't
  // touch React state and always produces a distinct string.
  const nextKey = useRef(0);
  const makeKey = () => `pf-${Date.now()}-${nextKey.current++}`;

  const validateFile = (f: File): string | null => {
    if (!ALLOWED_MIME.has(f.type) && !ALLOWED_EXT.test(f.name)) {
      return `Unsupported file type: ${f.name}. Only JPG, PNG or PDF are allowed.`;
    }
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      return `${f.name} is larger than ${MAX_FILE_MB} MB.`;
    }
    return null;
  };

  const handleFileChange = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(evt.target.files ?? []);
    evt.target.value = ""; // reset so selecting the same file again fires onChange

    for (const f of files) {
      const err = validateFile(f);
      if (err) {
        setUploadError(err);
        return;
      }
    }
    setUploadError(null);

    if (mode === "edit" && courseId) {
      // Upload each file immediately — the row is persisted, so
      // course_materials rows can be created right away.
      for (const f of files) {
        const fd = new FormData();
        fd.append("courseId", String(courseId));
        fd.append("file", f);
        fd.append("title", f.name);
        try {
          const res = await uploadMaterial(fd).unwrap();
          if (!res.status) {
            showErrorMessage(res.message || `Failed to upload ${f.name}`);
          }
        } catch {
          showErrorMessage(`Failed to upload ${f.name}`);
        }
      }
      onMaterialsChanged?.();
    } else {
      // Create mode — defer the upload until the parent page has an id.
      setPendingItems((prev) => [
        ...prev,
        ...files.map<PendingItem>((f) => ({ key: makeKey(), file: f, status: "ready" })),
      ]);
    }
  };

  /**
   * Upload a single pending item and mutate its status in-place.
   * Returns true on success. Used by both the batched
   * `uploadPendingForCreate` and the per-row retry button so the
   * status-transition rules only exist in one place.
   */
  const uploadOnePending = async (item: PendingItem, courseIdForUpload: number): Promise<boolean> => {
    setPendingItems((prev) =>
      prev.map((p) => (p.key === item.key ? { ...p, status: "uploading", error: undefined } : p)),
    );
    const fd = new FormData();
    fd.append("courseId", String(courseIdForUpload));
    fd.append("file", item.file);
    fd.append("title", item.file.name);
    try {
      const res = await uploadMaterial(fd).unwrap();
      if (res.status) {
        // Success — remove the item from the pending list. The
        // persisted attachment will appear via the course cache
        // invalidation (uploadCourseMaterial invalidates the
        // `course` tag → useGetCourseQuery refetches on next visit).
        setPendingItems((prev) => prev.filter((p) => p.key !== item.key));
        return true;
      }
      setPendingItems((prev) =>
        prev.map((p) =>
          p.key === item.key
            ? { ...p, status: "error", error: res.message || "Upload failed." }
            : p,
        ),
      );
      return false;
    } catch (e: any) {
      setPendingItems((prev) =>
        prev.map((p) =>
          p.key === item.key
            ? { ...p, status: "error", error: e?.message || "Upload failed." }
            : p,
        ),
      );
      return false;
    }
  };

  const uploadPendingForCreate = async (newCourseId: number): Promise<CourseUploadOutcome> => {
    createdCourseIdRef.current = newCourseId;
    // Snapshot the current queue so status updates during the loop
    // don't shift what we iterate over. Skip anything already
    // uploading (defensive — the caller ensures single-flight).
    const snapshot = pendingItems.filter((p) => p.status !== "uploading");
    let failedCount = 0;
    for (const item of snapshot) {
      // eslint-disable-next-line no-await-in-loop
      const ok = await uploadOnePending(item, newCourseId);
      if (!ok) failedCount++;
    }
    return { ok: failedCount === 0, failedCount };
  };

  /**
   * Retry a single failed upload. Only enabled once the course row
   * has been created (i.e. after at least one Save Course attempt),
   * which is when `createdCourseIdRef` is populated.
   */
  const retryPendingItem = async (item: PendingItem) => {
    const cid = createdCourseIdRef.current;
    if (!cid) {
      // No course id yet — the user hasn't clicked Save. Just clear
      // the error so the row falls back to Ready, and Save will pick
      // it up again on the next attempt.
      setPendingItems((prev) =>
        prev.map((p) => (p.key === item.key ? { ...p, status: "ready", error: undefined } : p)),
      );
      return;
    }
    await uploadOnePending(item, cid);
  };

  const persistVideoUrlForCreate = async (newCourseId: number, videoUrl: string) => {
    const trimmed = (videoUrl || "").trim();
    if (!trimmed) return;
    try {
      await createMaterial({
        courseId: newCourseId,
        title: "Video",
        materialType: "video",
        externalUrl: trimmed,
        orderNumber: 1,
        isPublished: true,
      }).unwrap();
    } catch {
      showErrorMessage("Course was saved but the video URL could not be recorded.");
    }
  };

  // Parent (Create page) needs to call these two after the save mutation
  // resolves — the course id doesn't exist yet at submit time.
  React.useImperativeHandle(
    ref,
    () => ({
      uploadPendingForCreate,
      persistVideoUrlForCreate,
      // `hasPendingFiles` now reports whether ANY item is still
      // waiting to be persisted — either untried (`ready`) or a
      // previous attempt failed (`error`). The parent uses this to
      // decide whether it's safe to navigate away after Save.
      hasPendingFiles: () =>
        pendingItems.some((p) => p.status !== "uploading"),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingItems],
  );

  // Existing video material (only one supported for now — the "hero" video).
  const videoMaterial = useMemo(
    () => existingMaterials.find((m) => (m.materialType || "").toLowerCase() === "video") ?? null,
    [existingMaterials],
  );

  // Sync formik.videoUrl from server on first load — only if the parent
  // hasn't already typed something.
  useEffect(() => {
    if (mode === "edit" && videoMaterial && !formik.values.videoUrl) {
      formik.setFieldValue("videoUrl", videoMaterial.externalUrl ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoMaterial]);

  const attachments = useMemo(
    () =>
      existingMaterials.filter((m) => {
        const t = (m.materialType || "").toLowerCase();
        return t === "pdf" || t === "image" || t === "document";
      }),
    [existingMaterials],
  );

  const handleDeleteAttachment = async (id: number) => {
    try {
      const res = await deleteMaterial(String(id)).unwrap();
      if (res.status) {
        showMessage(res.message);
        onMaterialsChanged?.();
      } else {
        showErrorMessage(res.message);
      }
    } catch {
      showErrorMessage("Failed to delete attachment.");
    }
  };

  // Missing-field validation surfaced on submit attempt. Formik touches
  // fields only when the user interacts; missing-required errors otherwise
  // stay silent. Show them at the top and inline so nothing fails silently.
  // Each entry carries the data-testid the scroll-and-focus guard uses to
  // pop the first offending field into view.
  type ValidationError = { label: string; testid: string };
  const validationErrors = useMemo<ValidationError[]>(() => {
    const list: ValidationError[] = [];
    if (!formik.values.courseName.trim()) {
      list.push({ label: "Course name is required.", testid: "course-name-field" });
    }
    if (formik.values.endDate && formik.values.startDate) {
      const s = parseDateOnly(formik.values.startDate);
      const e = parseDateOnly(formik.values.endDate);
      if (s && e && e.getTime() < s.getTime()) {
        list.push({
          label: "End date must be on or after start date.",
          testid: "course-end-date-field",
        });
      }
    }
    return list;
  }, [formik.values.courseName, formik.values.startDate, formik.values.endDate]);

  // Local "user has attempted submit" flag, since we intercept the form
  // submit before formik.handleSubmit runs — formik.submitCount would only
  // increment if we passed through to it, which we can't (parent's onSubmit
  // has no validation gate). Same shape as ProgramLibrary/Create/index.tsx
  // and the Settings editors.
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const missingCourseName = attemptedSubmit && !formik.values.courseName.trim();
  const endDateInvalid =
    attemptedSubmit &&
    !!formik.values.endDate &&
    !!formik.values.startDate &&
    (() => {
      const s = parseDateOnly(formik.values.startDate);
      const e = parseDateOnly(formik.values.endDate);
      return !!(s && e && e.getTime() < s.getTime());
    })();

  const attemptSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAttemptedSubmit(true);
    if (validationErrors.length > 0) {
      // Pattern lifted from Pages/ProgramLibrary/Create/index.tsx:428-435
      // (also used by the Settings Prompt-Templates and Workflows editors).
      // setTimeout(0) so React commits the setAttemptedSubmit state and the
      // inline errors render before we look for the node.
      setTimeout(() => {
        const first = validationErrors[0];
        const node = document.querySelector<HTMLElement>(`[data-testid="${first.testid}"]`);
        if (node) {
          node.scrollIntoView({ behavior: "smooth", block: "center" });
          const input = node.querySelector<HTMLInputElement>("input");
          input?.focus();
        }
      }, 0);
      return;
    }
    formik.handleSubmit();
  };

  return (
    <form onSubmit={attemptSubmit} noValidate>
      {/* Missing-fields alert — appears only after the user attempts submit. */}
      {attemptedSubmit && validationErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Fix the following before saving:{" "}
          <strong>{validationErrors.map((v) => v.label).join(" ")}</strong>
        </Alert>
      )}
      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}

      {/* ── COURSE INFORMATION CARD ────────────────────────────────
             One card, four logical groups. The visual dividers +
             uppercase section eyebrows create clear hierarchy
             without fragmenting the form into separate walls. */}
      <Box
        sx={{
          ...surfaceSx,
          p: { xs: 2.25, md: 3 },
          mb: 2,
          borderRadius: 3,
          boxShadow: "0 1px 2px rgba(15,23,42,0.03), 0 8px 24px rgba(31,111,235,0.04)",
        }}
      >
        {/* Card-level header — module identity + supporting line. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: tint12(B13.primary),
              color: B13.primary,
              "& svg": { fontSize: 22 },
              flexShrink: 0,
            }}
          >
            <MenuBookOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...B13TypeScale.h3, letterSpacing: "-0.005em" }}>
              Course information
            </Typography>
            <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink3, mt: 0.35 }}>
              Core details families and staff will see.
            </Typography>
          </Box>
        </Box>

        {/* ─ BASIC DETAILS ─────────────────────────────────────── */}
        <FormSectionHeader
          first
          icon={<EditNoteOutlinedIcon />}
          eyebrow="Basic details"
          hint="What the course is called and what it covers."
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <TextField
              data-testid="course-name-field"
              label="Course name"
              required
              fullWidth
              sx={formFieldSx}
              value={formik.values.courseName}
              onChange={(e) => formik.setFieldValue("courseName", e.target.value)}
              error={missingCourseName}
              helperText={missingCourseName ? "Course name is required." : " "}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              label="Course code"
              fullWidth
              sx={formFieldSx}
              placeholder="e.g., MATH101"
              value={formik.values.courseCode}
              onChange={(e) => formik.setFieldValue("courseCode", e.target.value)}
              helperText=" "
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={6}
              sx={formFieldSx}
              placeholder="Describe this course for families. Plain text; line breaks preserved."
              value={formik.values.description}
              onChange={(e) => formik.setFieldValue("description", e.target.value)}
              helperText="Plain text with line breaks. Rich formatting is not enabled."
            />
          </Grid>
        </Grid>

        {/* ─ ACADEMIC SETUP ───────────────────────────────────── */}
        <FormSectionHeader
          icon={<SchoolOutlinedIcon />}
          eyebrow="Academic setup"
          hint="Choose where this course belongs and who teaches it."
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth sx={formFieldSx}>
              <InputLabel>Grade</InputLabel>
              <Select
                label="Grade"
                value={formik.values.gradeId}
                onChange={(e) => formik.setFieldValue("gradeId", e.target.value)}
              >
                <MenuItem value="">Not set</MenuItem>
                {gradeList.map((g: any) => (
                  <MenuItem key={g.id} value={String(g.id)}>
                    {g.grade}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth sx={formFieldSx}>
              <InputLabel>Subject</InputLabel>
              <Select
                label="Subject"
                value={formik.values.subjectId}
                onChange={(e) => formik.setFieldValue("subjectId", e.target.value)}
              >
                <MenuItem value="">Not set</MenuItem>
                {subjectList.map((s: any) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.subject_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth sx={formFieldSx}>
              <InputLabel>Staff</InputLabel>
              <Select
                label="Staff"
                value={formik.values.staffId}
                onChange={(e) => formik.setFieldValue("staffId", e.target.value)}
              >
                <MenuItem value="">Not set</MenuItem>
                {staffList.map((s: any) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {`${s.teamFirstName ?? ""} ${s.teamLastName ?? ""}`.trim() || `Staff #${s.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* ─ COURSE PERIOD ────────────────────────────────────── */}
        <FormSectionHeader
          icon={<CalendarMonthOutlinedIcon />}
          eyebrow="Course period"
          hint="Set publish status and when the course runs."
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth sx={formFieldSx}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={formik.values.status}
                onChange={(e) => formik.setFieldValue("status", e.target.value)}
                renderValue={(v) => {
                  // Small coloured status dot inside the collapsed
                  // Select value so Draft/Active read at a glance.
                  // Values themselves are unchanged.
                  const val = String(v || "");
                  const meta: Record<string, { label: string; dot: string }> = {
                    draft:    { label: "Draft",    dot: "#F59E0B" },
                    active:   { label: "Active",   dot: "#16A34A" },
                    archived: { label: "Archived", dot: "#94A3B8" },
                    inactive: { label: "Inactive", dot: "#64748B" },
                  };
                  const m = meta[val] ?? { label: val || "—", dot: "#94A3B8" };
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: m.dot }} />
                      <span>{m.label}</span>
                    </Box>
                  );
                }}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Premium date pickers — reuse the app-wide AppDatePicker so
              Course Create picks up the same branded calendar every
              other form uses. Dates still flow through the same
              `YYYY-MM-DD` dateOnly helpers, so the API payload and
              timezone semantics are byte-identical to the previous
              native <input type="date"> version.

              Start > End safety: if the user picks a Start Date that
              is AFTER the current End Date, clear End Date so we
              never silently ship an invalid range. End Date's
              `minDate` is bound to Start Date, so once Start is set
              the parent can't pick an earlier End from the calendar
              in the first place. */}
          <Grid item xs={12} md={4}>
            <AppDatePicker
              label="Start date"
              value={formik.values.startDate}
              onChange={(next) => {
                const start = formatDateOnly(next);
                formik.setFieldValue("startDate", start);
                // If the new start pushes past the current end, drop
                // end so the user has to reselect intentionally.
                // YYYY-MM-DD strings sort lexically, so a plain
                // string compare is timezone-safe and adapter-free.
                const currentEnd = formik.values.endDate;
                if (start && currentEnd && start > currentEnd) {
                  formik.setFieldValue("endDate", "");
                }
              }}
              helperText={
                formik.values.startDate ? `Local: ${presentDateOnly(formik.values.startDate)}` : " "
              }
              slotProps={{ textField: { sx: formFieldSx } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <AppDatePicker
              label="End date"
              value={formik.values.endDate}
              onChange={(next) => formik.setFieldValue("endDate", formatDateOnly(next))}
              minDate={formik.values.startDate || null}
              error={endDateInvalid}
              helperText={
                endDateInvalid
                  ? "End date must be on or after start date."
                  : formik.values.endDate
                  ? `Local: ${presentDateOnly(formik.values.endDate)}`
                  : " "
              }
              slotProps={{
                textField: {
                  // Preserve the existing E2E hook — the previous
                  // TextField carried this data-testid so keep tests
                  // pointing at the same node identity.
                  "data-testid": "course-end-date-field",
                  sx: formFieldSx,
                },
              }}
            />
          </Grid>
        </Grid>

        {/* ─ COURSE MEDIA ─────────────────────────────────────── */}
        <FormSectionHeader
          icon={<OndemandVideoOutlinedIcon />}
          eyebrow="Course media"
          hint="An optional video walkthrough embedded on the course page."
        />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Video URL (YouTube or Vimeo)"
              fullWidth
              sx={formFieldSx}
              placeholder="https://www.youtube.com/watch?v=…"
              value={formik.values.videoUrl}
              onChange={(e) => formik.setFieldValue("videoUrl", e.target.value)}
              helperText="Saved as a course material. Recognised YouTube / Vimeo URLs embed on the course page; anything else is shown as a link."
            />
          </Grid>
        </Grid>
      </Box>

      {/* ── ATTACHMENTS CARD ───────────────────────────────────────
             Same visual language as the Course Information card (radius,
             shadow, module icon-tile header). The upload button is the
             single obvious action; if there's a limit / file-type
             restriction we surface it as one clean caption line. */}
      <Box
        sx={{
          ...surfaceSx,
          p: { xs: 2.25, md: 3 },
          mb: 2,
          borderRadius: 3,
          boxShadow: "0 1px 2px rgba(15,23,42,0.03), 0 8px 24px rgba(31,111,235,0.04)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: tint12(B13.primary),
              color: B13.primary,
              "& svg": { fontSize: 22 },
              flexShrink: 0,
            }}
          >
            <AttachFileOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ ...B13TypeScale.h3, letterSpacing: "-0.005em" }}>
              Attachments
            </Typography>
            <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink3, mt: 0.35 }}>
              JPG, PNG or PDF up to {MAX_FILE_MB} MB per file.
            </Typography>
          </Box>
          {/* Header-level upload button so the primary attachment
              action is always visible even when the list has grown. */}
          <Button
            component="label"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            disabled={uploading}
            sx={{
              ...secondaryBtnSx,
              minHeight: 44,
              borderRadius: "10px",
              px: 2,
              flexShrink: 0,
            }}
          >
            {uploading ? "Uploading…" : "Add attachment"}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
              onChange={handleFileChange}
            />
          </Button>
        </Box>

        {/* Existing attachments (Edit only). Row treatment matches
            the pending-row look so both feel like the same list. */}
        {attachments.length > 0 && (
          <Stack spacing={1} sx={{ mb: pendingItems.length ? 1 : 0 }}>
            {attachments.map((m) => {
              const type = (m.materialType || "").toLowerCase();
              const Icon = type === "pdf" ? PictureAsPdfIcon : type === "image" ? ImageIcon : DescriptionIcon;
              const iconColor =
                type === "pdf" ? "#DC2626" : type === "image" ? "#0EA5E9" : B13Ink.ink3;
              return (
                <Box
                  key={m.id}
                  sx={{
                    p: 1.25,
                    border: `1px solid ${B13.line}`,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    bgcolor: "#FFFFFF",
                    transition: "border-color 160ms ease, box-shadow 160ms ease",
                    "&:hover": {
                      borderColor: tint12(B13.primary),
                      boxShadow: "0 4px 12px rgba(15,23,42,0.05)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: `${iconColor}14`,
                      color: iconColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "& svg": { fontSize: 20 },
                      flexShrink: 0,
                    }}
                  >
                    <Icon />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={B13TypeScale.bodyStrong} noWrap>
                      {m.title || "Attachment"}
                    </Typography>
                    <Typography
                      sx={{
                        ...B13TypeScale.caption,
                        color: B13Ink.ink3,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                      }}
                    >
                      {type || "file"}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteAttachment(m.id)}
                    aria-label={`Delete ${m.title || "attachment"}`}
                    title="Delete"
                    sx={{
                      color: B13Ink.ink3,
                      "&:hover": { color: "#E11D48", bgcolor: "#FEE2E2" },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Stack>
        )}

        {/* Pending items (Create only) — each row reflects the file's
            real lifecycle status so users can see when a file is
            waiting, actively uploading, or has failed. Rendering
            derives its label + border colour from `item.status`; no
            state stays "Pending" indefinitely because Save Course
            drives every ready item through the transition. */}
        {pendingItems.length > 0 && (
          <Stack spacing={1}>
            {pendingItems.map((item) => {
              const mime = (item.file.type || "").toLowerCase();
              const isPdf   = mime === "application/pdf" || /\.pdf$/i.test(item.file.name);
              const isImage = mime.startsWith("image/") || /\.(jpe?g|png|gif|webp)$/i.test(item.file.name);
              const Icon = isPdf ? PictureAsPdfIcon : isImage ? ImageIcon : InsertDriveFileOutlinedIcon;
              const iconColor = isPdf ? "#DC2626" : isImage ? "#0EA5E9" : B13Ink.ink3;

              // Human-readable size line — no additional network
              // request, just File.size formatting.
              const humanSize =
                item.file.size < 1024
                  ? `${item.file.size} B`
                  : item.file.size < 1024 * 1024
                  ? `${(item.file.size / 1024).toFixed(0)} KB`
                  : `${(item.file.size / (1024 * 1024)).toFixed(1)} MB`;
              const kindLabel = isPdf ? "PDF" : isImage ? "PNG/JPG" : "FILE";

              // Status-driven styling. Colours only shift on the
              // status pill + border; the body of the row stays
              // white so the file name remains highly readable.
              const isReady     = item.status === "ready";
              const isUploading = item.status === "uploading";
              const isError     = item.status === "error";

              const rowBorder = isError
                ? "#FCA5A5"
                : isUploading
                ? tint12(B13.primary)
                : tint12(B13.primary);
              const rowBg = isError ? "#FEF2F2" : "#F4F8FF";
              const statusLabel = isReady
                ? "Ready to upload"
                : isUploading
                ? "Uploading…"
                : "Upload failed";
              const statusColor = isError ? "#B91C1C" : B13.primary;

              return (
                <Box
                  key={item.key}
                  sx={{
                    p: 1.25,
                    border: `1px dashed ${rowBorder}`,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    bgcolor: rowBg,
                    transition: "border-color 160ms ease, background-color 160ms ease",
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: `${iconColor}14`,
                      color: iconColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "& svg": { fontSize: 20 },
                      flexShrink: 0,
                    }}
                  >
                    <Icon />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ ...B13TypeScale.bodyStrong }} noWrap title={item.file.name}>
                      {item.file.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.35 }}>
                      {/* Status glyph — spinner while uploading, error
                          triangle on failure, no glyph on ready. */}
                      {isUploading && <CircularProgress size={12} sx={{ color: statusColor }} />}
                      {isError && (
                        <ErrorOutlineOutlinedIcon sx={{ fontSize: 14, color: statusColor }} />
                      )}
                      <Typography
                        sx={{
                          ...B13TypeScale.caption,
                          fontWeight: 700,
                          color: statusColor,
                          letterSpacing: 0.2,
                        }}
                      >
                        {statusLabel}
                      </Typography>
                      <Typography
                        sx={{
                          ...B13TypeScale.caption,
                          color: B13Ink.ink3,
                        }}
                      >
                        · {kindLabel} · {humanSize}
                      </Typography>
                    </Box>
                    {isError && item.error && (
                      <Typography
                        sx={{
                          ...B13TypeScale.caption,
                          color: "#B91C1C",
                          mt: 0.35,
                          fontStyle: "italic",
                        }}
                      >
                        {item.error}
                      </Typography>
                    )}
                  </Box>

                  {/* Retry — only on error. Uses the courseId captured
                      from the previous Save attempt so we don't create
                      a second course row. */}
                  {isError && (
                    <IconButton
                      size="small"
                      onClick={() => retryPendingItem(item)}
                      aria-label={`Retry upload for ${item.file.name}`}
                      title="Retry"
                      sx={{
                        color: B13.primary,
                        "&:hover": { color: B13.primary, bgcolor: tint12(B13.primary) },
                      }}
                    >
                      <RefreshOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                  {/* Remove — hidden while a row is uploading so users
                      can't drop a file mid-flight. */}
                  {!isUploading && (
                    <IconButton
                      size="small"
                      onClick={() =>
                        setPendingItems((prev) => prev.filter((p) => p.key !== item.key))
                      }
                      aria-label={`Remove ${item.file.name}`}
                      title="Remove"
                      sx={{
                        color: B13Ink.ink3,
                        "&:hover": { color: B13.primary, bgcolor: tint12(B13.primary) },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}

        {/* Empty-list hint — only when nothing has been uploaded or
            queued. Keeps the card looking intentional instead of
            "just a button". */}
        {attachments.length === 0 && pendingItems.length === 0 && (
          <Box
            sx={{
              mt: 0.5,
              p: 2.25,
              borderRadius: 2,
              border: `1px dashed ${tint12(B13.primary)}`,
              bgcolor: "#F4F8FF",
              textAlign: "center",
            }}
          >
            <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2 }}>
              No attachments yet. Use <strong>Add attachment</strong> to upload course materials.
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── STICKY ACTION BAR ──────────────────────────────────────
             Anchors Cancel + Save Course to the bottom of the
             viewport so the primary CTA is always one click away,
             regardless of how long the form has grown. A reassuring
             line clarifies save semantics ("Changes are saved when
             you click Save course."). The bar has a solid white
             background and a soft upward shadow so it never covers
             content behind a translucent surface — safe over the
             app's chat widget too since the button row sits in the
             natural page flow above the widget's fixed position. */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 5,
          mt: 2,
          mx: { xs: -2, md: -3 },
          px: { xs: 2, md: 3 },
          py: 1.5,
          bgcolor: "#FFFFFF",
          borderTop: `1px solid ${B13.line}`,
          boxShadow: "0 -6px 18px rgba(15,23,42,0.05)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 1.5,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink3 }}>
            Changes are saved when you click <strong>Save course</strong>.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, justifyContent: { xs: "space-between", md: "flex-end" } }}>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={() => navigate("/course/list")}
              disabled={submitting}
              sx={{
                ...secondaryBtnSx,
                minHeight: 44,
                borderRadius: "10px",
                px: 2,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              startIcon={submitting ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <SaveIcon />}
              disabled={submitting}
              sx={{
                ...primaryBtnSx,
                minHeight: 44,
                borderRadius: "10px",
                px: 2.25,
                boxShadow: "0 4px 10px rgba(31,111,235,0.24)",
                "&:hover": {
                  bgcolor: B13.primary,
                  filter: "brightness(0.95)",
                  boxShadow: "0 6px 14px rgba(31,111,235,0.30)",
                },
              }}
            >
              {submitLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
});

export default CourseFormBody;

// Re-export for parents that want to type their state.
export const DEFAULT_COURSE_FORM_VALUES: CourseFormValues = {
  id: null,
  courseName: "",
  courseCode: "",
  description: "",
  gradeId: "",
  subjectId: "",
  staffId: "",
  status: "draft",
  startDate: todayLocal(),
  endDate: "",
  thumbnail: "",
  videoUrl: "",
};
